import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import { attachAuthInterceptors } from "../utils/axiosAuth";

// Define types for the API
export interface AssessmentData {
  patientName: string;
  facilityName: string;
  dateOfBirth: string;
  admissionDate: string;
  lengthOfStay: string;
  acuteAdmission: string;
  comorbidities: string[];
  emergencyVisits: string;
  assessedBy: string;
  notes?: string;
}

export interface Assessment extends AssessmentData {
  id: string;
  totalScore: number;
  readmissionProbability: string;
  lengthOfStayPoints: number;
  acuteAdmissionPoints: number;
  comorbidityPoints: number;
  emergencyVisitsPoints: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentFilters {
  patientName?: string;
  facilityName?: string;
  assessedBy?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface PaginationInfo {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  itemsPerPage: number;
}

export interface Statistics {
  overall: {
    totalAssessments: number;
    averageScore: number;
    minScore: number;
    maxScore: number;
    lowReadmission: number;
    moderateReadmission: number;
    highReadmission: number;
  };
  topFacilities: Array<{
    _id: string;
    count: number;
    averageScore: number;
  }>;
}

export interface HealthCheck {
  status: string;
  timestamp: string;
  version: string;
}

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:5001/api",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Log the API base URL for debugging
console.log("Authenticated API Base URL:", import.meta.env.VITE_API_URL || "http://localhost:5001/api");

api.interceptors.request.use(
  (config) => {
    console.log("Authenticated API Request:", config.method?.toUpperCase(), config.url);
    return config;
  },
  (error: AxiosError) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log("Authenticated API Response:", response.status, response.config.url);
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      const { status, data, config } = error.response;
      console.error(`Authenticated API Error ${status}:`, {
        url: config?.url,
        method: config?.method,
        message: (data as any)?.message,
        data: data
      });
      
      switch (status) {
        case 400:
          console.error("Bad Request:", (data as any)?.message);
          break;
        case 401:
          console.error("Unauthorized:", (data as any)?.message);
          break;
        case 404:
          console.error("Not Found:", (data as any)?.message);
          break;
        case 500:
          console.error("Server Error:", (data as any)?.message);
          break;
        default:
          console.error("API Error:", (data as any)?.message);
      }
    } else if (error.request) {
      // Network error
      console.error("Network Error:", {
        message: "Unable to connect to server",
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
    } else {
      // Other error
      console.error("Request Setup Error:", error.message);
    }
    
    return Promise.reject(error);
  }
);

attachAuthInterceptors(api);

// Authenticated API service functions
export const authenticatedLaceToolAPI = {
  // Create a new LACE tool assessment
  createAssessment: async (assessmentData: AssessmentData): Promise<Assessment> => {
    try {
      const response = await api.post("/lace-tool/assessments", assessmentData);
      // Transform the backend response format to match frontend expectations
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all assessments with optional filters and server-side pagination
  getAssessments: async (params: AssessmentFilters = {}): Promise<{ assessments: Assessment[]; pagination: PaginationInfo }> => {
    try {
      // Map frontend parameters to backend parameters
      const backendParams: any = {};
      if (params.patientName) backendParams.patientName = params.patientName;
      if (params.facilityName) backendParams.facilityName = params.facilityName;
      if (params.assessedBy) backendParams.assessedBy = params.assessedBy;
      if (params.dateFrom) backendParams.startDate = params.dateFrom;
      if (params.dateTo) backendParams.endDate = params.dateTo;
      if (params.page) backendParams.page = params.page;
      if (params.limit) backendParams.limit = params.limit;

      const response = await api.get("/lace-tool/assessments", { params: backendParams });
      // Transform the backend response format to match frontend expectations
      return {
        assessments: response.data.data || [],
        pagination: response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        }
      };
    } catch (error) {
      throw error;
    }
  },

  // Get a specific assessment by ID
  getAssessment: async (id: string): Promise<Assessment> => {
    try {
      const response = await api.get(`/lace-tool/assessments/${id}`);
      // Transform the backend response format to match frontend expectations
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Update an assessment
  updateAssessment: async (id: string, assessmentData: Partial<AssessmentData>): Promise<Assessment> => {
    try {
      const response = await api.put(`/lace-tool/assessments/${id}`, assessmentData);
      // Transform the backend response format to match frontend expectations
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete an assessment
  deleteAssessment: async (id: string): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/lace-tool/assessments/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get assessment statistics
  getStatistics: async (): Promise<{ data: Statistics }> => {
    try {
      const response = await api.get("/lace-tool/statistics");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Health check
  healthCheck: async (): Promise<HealthCheck> => {
    try {
      const response = await api.get("/health");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download assessment as PDF
  downloadAssessmentPDF: async (assessmentId: string): Promise<Blob> => {
    try {
      const response = await api.get(`/lace-tool/assessments/${assessmentId}/pdf`, {
        responseType: "blob"
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download dashboard as PDF
  downloadDashboardPDF: async (filters?: {
    facilityName?: string;
    doctorName?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> => {
    try {
      const response = await api.get("/reports/dashboard/pdf", {
        params: filters,
        responseType: "blob",
        timeout: 30000, // Extended timeout for PDF generation
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default api;
